"use client";

/**
 * `<FAQRow>` — accordion row with expand/collapse animation. The first
 * row defaults to open so the FAQ section opens with at least one answer
 * visible.
 */

import { memo, useId, useState } from "react";

interface FAQRowProps {
  q: string;
  a: string;
  defaultOpen?: boolean;
}

function FAQRowImpl({ q, a, defaultOpen }: FAQRowProps) {
  const [open, setOpen] = useState(!!defaultOpen);
  const panelId = useId();

  return (
    <div style={{ background: "var(--color-surface)" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        style={{
          width: "100%",
          padding: "22px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          textAlign: "left",
          cursor: "pointer",
          background: "transparent",
          border: "none",
          color: "var(--color-text)",
        }}
      >
        <span
          style={{
            fontSize: 15.5,
            color: "var(--color-text)",
            fontWeight: 500,
            letterSpacing: "-0.005em",
          }}
        >
          {q}
        </span>
        <span
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            border: "1px solid var(--color-line)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            marginLeft: 16,
            transition: "transform 0.2s",
            transform: open ? "rotate(45deg)" : "none",
            color: "var(--color-muted)",
          }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            aria-hidden="true"
          >
            <path
              d="M5 1 V9 M1 5 H9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-hidden={!open}
        style={{
          maxHeight: open ? 600 : 0,
          overflow: "hidden",
          transition: "max-height 0.3s",
        }}
      >
        <div
          style={{
            padding: "0 28px 22px",
            fontSize: 14.5,
            color: "var(--color-muted)",
            lineHeight: 1.65,
            maxWidth: 720,
          }}
        >
          {a}
        </div>
      </div>
    </div>
  );
}

export const FAQRow = memo(FAQRowImpl);
